import { CheckCircleOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, Image, Input, Modal, Popconfirm, Select, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { ImageUpload } from "@/shared/components/ImageUpload";
import { resolveImageUrl } from "@/shared/utils/resolveImageUrl";
import {
	useAddTaskLocations,
	useAddTaskPlants,
	useCompleteTaskLocation,
	useCompleteTaskPlant,
	useRemoveTaskLocation,
	useRemoveTaskPlant,
} from "../hooks/useTasks";
import type { CompleteTaskItemRequest, Task, TaskLocation, TaskPlant } from "../types";
import { PlantingMultiSelect } from "./PlantingMultiSelect";
import type { TaskLocationDraft } from "./TaskLocationListInput";
import { TaskLocationListInput } from "./TaskLocationListInput";

function formatDateTime(date: string | null | undefined): string {
	if (!date) {
		return "—";
	}
	return new Date(date).toLocaleString("uz-UZ", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	});
}

interface CompleteFormValues {
	photoReportFileId?: string;
	performedByStaffIds?: string[];
	comment?: string;
}

type CompletionRow = (TaskLocation | TaskPlant) & { rowLabel: string };

interface TaskCompletionListProps {
	task: Task;
}

export function TaskCompletionList({ task }: TaskCompletionListProps) {
	const [completeForm] = Form.useForm<CompleteFormValues>();
	const [activeRow, setActiveRow] = useState<CompletionRow | null>(null);
	const [addModalOpen, setAddModalOpen] = useState(false);
	const [draftLocations, setDraftLocations] = useState<TaskLocationDraft[]>([]);
	const [draftPlantingIds, setDraftPlantingIds] = useState<string[]>([]);

	const { mutate: completeLocation, isPending: isCompletingLocation } = useCompleteTaskLocation();
	const { mutate: completePlant, isPending: isCompletingPlant } = useCompleteTaskPlant();
	const { mutate: removeLocation } = useRemoveTaskLocation();
	const { mutate: removePlant } = useRemoveTaskPlant();
	const { mutate: addLocations, isPending: isAddingLocations } = useAddTaskLocations();
	const { mutate: addPlants, isPending: isAddingPlants } = useAddTaskPlants();

	const isLocationTarget = task.targetType === "LOCATION";
	const members = task.brigade?.members ?? [];

	const rows: CompletionRow[] = isLocationTarget
		? (task.locations ?? []).map((l, i) => ({
				...l,
				rowLabel: `${l.type === "POINT" ? "Nuqta" : "Maydon"} ${i + 1}`,
			}))
		: (task.plants ?? []).map((p) => ({ ...p, rowLabel: p.planting?.plantName ?? p.plantingId }));

	const openCompleteModal = (row: CompletionRow) => {
		completeForm.resetFields();
		setActiveRow(row);
	};
	const closeCompleteModal = () => setActiveRow(null);

	const handleCompleteSubmit = () => {
		completeForm
			.validateFields()
			.then((values) => {
				if (!activeRow) {
					return;
				}
				const data: CompleteTaskItemRequest = {
					photoReportFileId: values.photoReportFileId as string,
					performedByStaffIds: values.performedByStaffIds as string[],
					comment: values.comment || undefined,
				};
				if (isLocationTarget) {
					completeLocation(
						{ id: task.id, locationId: activeRow.id, data },
						{ onSuccess: closeCompleteModal }
					);
				} else {
					completePlant(
						{ id: task.id, plantId: activeRow.id, data },
						{ onSuccess: closeCompleteModal }
					);
				}
			})
			.catch(() => {
				// Validation failed - Form shows errors inline
			});
	};

	const openAddModal = () => {
		setDraftLocations([]);
		setDraftPlantingIds([]);
		setAddModalOpen(true);
	};
	const closeAddModal = () => setAddModalOpen(false);

	const handleAddSubmit = () => {
		if (isLocationTarget) {
			const locations = draftLocations
				.filter((entry) => entry.geometry)
				.map((entry) => ({ type: entry.type, geometry: entry.geometry }) as never);
			if (locations.length === 0) {
				return;
			}
			addLocations({ id: task.id, data: { locations } }, { onSuccess: closeAddModal });
		} else {
			if (draftPlantingIds.length === 0) {
				return;
			}
			addPlants(
				{ id: task.id, data: { plantingIds: draftPlantingIds } },
				{ onSuccess: closeAddModal }
			);
		}
	};

	const columns: ColumnsType<CompletionRow> = [
		{ title: isLocationTarget ? "Joylashuv" : "Ekin", dataIndex: "rowLabel", key: "rowLabel" },
		{
			title: "Holat",
			key: "isCompleted",
			width: 120,
			render: (_, row) =>
				row.isCompleted ? <Tag color="green">Bajarildi</Tag> : <Tag>Bajarilmagan</Tag>,
		},
		{
			title: "Bajardi",
			key: "completedBy",
			render: (_, row) =>
				row.completedBy.length > 0
					? row.completedBy.map((p) => (
							<Tag key={p.staffId} className="mb-1">
								{p.fullname}
							</Tag>
						))
					: "—",
		},
		{ title: "Vaqti", key: "completedAt", render: (_, row) => formatDateTime(row.completedAt) },
		{ title: "Izoh", key: "comment", render: (_, row) => row.comment ?? "—" },
		{
			title: "Foto",
			key: "photo",
			width: 80,
			align: "center",
			render: (_, row) =>
				row.photoReportFile?.url ? (
					<Image
						src={resolveImageUrl(row.photoReportFile.url)}
						alt="Foto hisobot"
						width={48}
						height={48}
						style={{ objectFit: "cover", borderRadius: 6 }}
					/>
				) : (
					"—"
				),
		},
		{
			title: "Amal",
			key: "action",
			width: 200,
			render: (_, row) =>
				row.isCompleted ? null : (
					<div className="flex items-center gap-2">
						<Button
							size="small"
							icon={<CheckCircleOutlined />}
							onClick={() => openCompleteModal(row)}
						>
							Bajarildi
						</Button>
						<Popconfirm
							title="Olib tashlash"
							description="Rostdan ham olib tashlamoqchimisiz?"
							onConfirm={() =>
								isLocationTarget
									? removeLocation({ id: task.id, locationId: row.id })
									: removePlant({ id: task.id, plantId: row.id })
							}
							okText="Ha"
							cancelText="Yo'q"
							okButtonProps={{ danger: true }}
						>
							<Button size="small" danger icon={<DeleteOutlined />} />
						</Popconfirm>
					</div>
				),
		},
	];

	return (
		<>
			<div className="flex justify-end mb-2">
				<Button size="small" icon={<PlusOutlined />} onClick={openAddModal}>
					{isLocationTarget ? "Joylashuv qo'shish" : "Ekin qo'shish"}
				</Button>
			</div>
			<Table
				columns={columns}
				dataSource={rows}
				rowKey="id"
				size="small"
				pagination={false}
				locale={{ emptyText: isLocationTarget ? "Joylashuvlar yo'q" : "Ekinlar yo'q" }}
			/>

			<Modal
				title="Bajarildi deb belgilash"
				open={!!activeRow}
				onCancel={closeCompleteModal}
				onOk={handleCompleteSubmit}
				confirmLoading={isCompletingLocation || isCompletingPlant}
				okText="Saqlash"
				cancelText="Bekor qilish"
				destroyOnHidden
			>
				<Form form={completeForm} layout="vertical" requiredMark={false} className="mt-4">
					<Form.Item
						name="performedByStaffIds"
						label="Kim bajardi"
						rules={[{ required: true, message: "Kamida bitta bajaruvchini tanlang" }]}
					>
						<Select
							mode="multiple"
							placeholder="Brigada a'zolarini tanlang"
							showSearch
							optionFilterProp="label"
							options={members.map((m) => ({ value: m.id, label: m.fullname }))}
						/>
					</Form.Item>
					<Form.Item
						name="photoReportFileId"
						label="Foto hisobot"
						rules={[{ required: true, message: "Foto yuklang" }]}
					>
						<ImageUpload />
					</Form.Item>
					<Form.Item name="comment" label="Izoh (ixtiyoriy)" rules={[{ max: 500 }]}>
						<Input.TextArea rows={3} placeholder="Izoh..." />
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title={isLocationTarget ? "Joylashuv qo'shish" : "Ekin qo'shish"}
				open={addModalOpen}
				onCancel={closeAddModal}
				onOk={handleAddSubmit}
				confirmLoading={isAddingLocations || isAddingPlants}
				okText="Qo'shish"
				cancelText="Bekor qilish"
				destroyOnHidden
				width={isLocationTarget ? 600 : 520}
			>
				{isLocationTarget ? (
					<TaskLocationListInput value={draftLocations} onChange={setDraftLocations} />
				) : (
					<PlantingMultiSelect
						organizationId={task.organizationId}
						value={draftPlantingIds}
						onChange={setDraftPlantingIds}
					/>
				)}
			</Modal>
		</>
	);
}
